import type { WorkspaceRole } from '@server/types';
import { createContext, useCallback, useContext, useMemo } from 'react';

export type { WorkspaceRole };

export interface WorkspaceInfo {
  id: string;
  name: string;
  role: WorkspaceRole;
  githubAppSlug?: string;
}

export interface WorkspaceContextValue {
  slug: string;
  workspace: WorkspaceInfo;
  isOwner: boolean;
  isAdmin: boolean;
  /** Build an absolute path under the current workspace: wp('/chats') -> '/w/my-team/chats' */
  wp: (subpath?: string) => string;
}

const WorkspaceCtx = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return ctx;
}

/**
 * Like useWorkspace, but returns null instead of throwing when called outside a WorkspaceProvider.
 * Use from components that may render in both workspace-scoped and workspace-agnostic contexts
 * (e.g., a chat block that can render in preview screens or tests without a workspace).
 */
export function useWorkspaceOrNull(): WorkspaceContextValue | null {
  return useContext(WorkspaceCtx);
}

export function WorkspaceProvider({
  slug,
  workspace,
  children,
}: {
  slug: string;
  workspace: WorkspaceInfo;
  children: React.ReactNode;
}): React.ReactElement {
  const isOwner = workspace.role === 'owner';
  const isAdmin = workspace.role === 'owner' || workspace.role === 'admin';

  const wp = useCallback(
    (subpath = '') => {
      const normalized = subpath.startsWith('/') ? subpath : `/${subpath}`;
      return `/w/${slug}${normalized === '/' ? '' : normalized}`;
    },
    [slug],
  );

  const value = useMemo(() => ({ slug, workspace, isOwner, isAdmin, wp }), [slug, workspace, isOwner, isAdmin, wp]);

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}
